import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { Component, ElementRef, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import snakeCase from 'lodash/snakeCase';
import { EMPTY, Observable, of, Subscription, timer } from 'rxjs';
import { expand, map, switchMap } from 'rxjs/operators';

import { bucketMaxLength, labelMaxLength, labelMinLength } from '../../../app.constants';
import { CoreApiService } from '../../../core-api.service';
import { LoggingService } from '../../../logging.service';
import { NotificationService } from '../../../notification.service';
import {
  ExportSettingsFields,
} from '../../../project-exports/project-export-dialog/project-export-settings/project-export-settings.enums';
import { ChmodEnum } from '../../../shared/enums/chmod.enum';
import { InputTypeEnum } from '../../../shared/enums/input-type.enum';
import { SelectOption, typeNumber } from '../../../shared/form.utils';
import { Group } from '../../../shared/models/group.model';
import { Project } from '../../../shared/models/project.model';
import { StorageCheckStatus, StorageConfig, StorageConfigType, XferStorage } from '../../../shared/models/xfer.model';
import { groupProjects } from '../../../shared/projects.utils';
import { sortGroupedOptions } from '../../../shared/select-with-groups.utils';
import { LocaleCopy, TranslationService } from '../../../translation.service';

const STATUS_CHECK_INTERVAL = 1; // seconds

type FormValue = string | number | object;

interface DialogData {
  storageId?: string
  projects: Record<string, Project>
  groups: Record<string, Group>
}

interface StatusCheck {
  status: StorageCheckStatus
  reason: string
}

export enum PermissionLevel {
  Site = 'site',
  Group = 'group',
  Project = 'project',
}

export enum StorageDialogControllers {
  AccessKey = 'accessKey',
  AccessKeyId = 'accessKeyId',
  Account = 'account',
  ApplicationCredentials = 'applicationCredentials',
  Bucket = 'bucket',
  ConnectorCreds = 'connectorCreds',
  Container = 'container',
  ContainerRef = 'containerRef',
  ExportsEnabled = 'exportsEnabled',
  Gid = 'gid',
  GroupPermission = 'groupPermission',
  Groups = 'groups',
  ImportsEnabled = 'importsEnabled',
  Label = 'label',
  MalwareScanning = 'malwareScanning',
  OtherPermission = 'others',
  Path = 'path',
  PermissionLevel = 'permissionLevel',
  Prefix = 'prefix',
  ProviderConfig = 'providerConfig',
  ProviderType = 'providerType',
  SecretAccessKey = 'secretAccessKey',
  Uid = 'uid',
  UserPermission = 'userPermission'
}

@Component({
  host: {
    'test-id': 'storage-edit-dialog',
  },
  selector: 'storage-edit-dialog',
  templateUrl: './storage-edit-dialog.component.html',
  styleUrls: ['./storage-edit-dialog.component.scss'],
})
export class StorageEditDialog implements OnInit, OnDestroy {
  @ViewChild('fileInput') fileInput: ElementRef<HTMLInputElement>;

  private readonly maxBucketNameLength = bucketMaxLength;
  private readonly passwordInitialValue = '***********';
  private readonly passwordFields = [
    StorageDialogControllers.AccessKey,
    StorageDialogControllers.ApplicationCredentials,
    StorageDialogControllers.AccessKeyId,
    StorageDialogControllers.SecretAccessKey,
  ];
  private readonly permissionsFields = [
    StorageDialogControllers.UserPermission,
    StorageDialogControllers.GroupPermission,
    StorageDialogControllers.OtherPermission,
  ];
  private readonly chownFields = [
    StorageDialogControllers.Uid,
    StorageDialogControllers.Gid,
  ];
  private readonly userPermissionDefaultOption = { label: 'storageEditDialog.fsChmod.options.readWriteDefault', value: ChmodEnum.ReadWrite };
  private readonly basePermissionDefaultOption = { label: 'storageEditDialog.fsChmod.options.readDefault', value: ChmodEnum.Read };
  private readonly numberInputs = [StorageDialogControllers.Gid, StorageDialogControllers.Uid];
  private readonly userPermissionOptions: SelectOption[] = [this.userPermissionDefaultOption];
  private readonly basePermissionOptions: SelectOption[] = [{ label: 'storageEditDialog.fsChmod.options.noAccess', value: ChmodEnum.NoAccess }, this.basePermissionDefaultOption, { label: 'storageEditDialog.fsChmod.options.readWrite', value: ChmodEnum.ReadWrite }];
  private readonly azureBlobSuffix = '.blob.core.windows.net';

  private hasSaved = false;
  private initialFormValue: Record<string, FormValue>;
  private storage: XferStorage // saved storage record
  private isValueChanged = false;
  private subscriptions = {
    statusCheck: Subscription.EMPTY,
    connectorCreds: Subscription.EMPTY,
    form: Subscription.EMPTY,
    providerType: Subscription.EMPTY,
    permissionLevel: Subscription.EMPTY,
  };
  private chmodDefaultValue = ChmodEnum.ReadWrite + ChmodEnum.Read + ChmodEnum.Read;

  form: FormGroup;
  statusCheck: StatusCheck = null;
  isSaving = false;

  protected readonly supplementalGroupIDValidators = [
    (value: string) => value.length <= 10,
    (value: string) => !isNaN(Number(value)) && value.trim() !== '',
  ];
  protected groupsValidatorsErrorMessage = 'The input must be numeric and must not exceed 10 characters.';
  protected isGroupsError = false;
  protected readonly fsInputsConfig = [
    { label: 'storageEditDialog.labels.path', placeholder: 'storageEditDialog.placeholders.path', description: 'storageEditDialog.descriptions.path', controller: StorageDialogControllers.Path, isRequired: true },
    { label: 'storageEditDialog.labels.uid', placeholder: 'storageEditDialog.placeholders.uid', description: 'storageEditDialog.descriptions.uid', controller: StorageDialogControllers.Uid, type: InputTypeEnum.Number },
    { label: 'storageEditDialog.labels.gid', placeholder: 'storageEditDialog.placeholders.gid', description: 'storageEditDialog.descriptions.gid', controller: StorageDialogControllers.Gid, type: InputTypeEnum.Number },
    { label: 'storageEditDialog.labels.sgid', placeholder: 'storageEditDialog.placeholders.sgid', description: 'storageEditDialog.descriptions.sgid', controller: StorageDialogControllers.Groups },
    { label: 'storageEditDialog.labels.userPermission', placeholder: 'storageEditDialog.placeholders.userPermission', description: 'storageEditDialog.descriptions.userPermission', controller: StorageDialogControllers.UserPermission, options: this.userPermissionOptions },
    { label: 'storageEditDialog.labels.groupPermission', placeholder: 'storageEditDialog.placeholders.groupPermission', description: 'storageEditDialog.descriptions.groupPermission', controller: StorageDialogControllers.GroupPermission, options: this.basePermissionOptions },
    { label: 'storageEditDialog.labels.otherPermission', placeholder: 'storageEditDialog.placeholders.otherPermission', description: 'storageEditDialog.descriptions.otherPermission', controller: StorageDialogControllers.OtherPermission, options: this.basePermissionOptions },
  ];
  protected readonly azInputsConfigBase = [
    { label: 'storageEditDialog.labels.account', placeholder: 'storageEditDialog.placeholders.account', description: 'storageEditDialog.descriptions.account', controller: StorageDialogControllers.Account, isRequired: true, info: 'storageEditDialog.info.account', errorInfo: 'storageEditDialog.info.accountError' },
    { label: 'storageEditDialog.labels.container', placeholder: 'storageEditDialog.placeholders.container', description: 'storageEditDialog.descriptions.container', controller: StorageDialogControllers.Container, isRequired: true },
    { label: 'storageEditDialog.labels.prefix', placeholder: 'storageEditDialog.placeholders.prefix', description: 'storageEditDialog.descriptions.azPrefix', controller: StorageDialogControllers.Prefix },
  ];
  protected readonly azInputsConfigAccessKey = [
    { label: 'storageEditDialog.labels.accessKey', placeholder: '', description: 'storageEditDialog.descriptions.accessKey', controller: StorageDialogControllers.AccessKey, isRequired: true, type: InputTypeEnum.Password },
  ];
  protected readonly gsInputsConfig = [
    { label: 'storageEditDialog.labels.bucket', placeholder: 'storageEditDialog.placeholders.bucket', description: 'storageEditDialog.descriptions.bucket', controller: StorageDialogControllers.Bucket, isRequired: true },
    { label: 'storageEditDialog.labels.prefix', placeholder: 'storageEditDialog.placeholders.prefix', description: 'storageEditDialog.descriptions.prefix', controller: StorageDialogControllers.Prefix },
    { label: 'storageEditDialog.labels.applicationCredentials', placeholder: '', description: 'storageEditDialog.descriptions.applicationCredentials', controller: StorageDialogControllers.ApplicationCredentials, isRequired: true, type: InputTypeEnum.Password },
  ];
  protected readonly s3InputsConfigBase = [
    { label: 'storageEditDialog.labels.bucket', placeholder: 'storageEditDialog.placeholders.bucket', description: 'storageEditDialog.descriptions.bucket', controller: StorageDialogControllers.Bucket, isRequired: true },
    { label: 'storageEditDialog.labels.prefix', placeholder: 'storageEditDialog.placeholders.prefix', description: 'storageEditDialog.descriptions.prefix', controller: StorageDialogControllers.Prefix },
  ];
  protected readonly s3InputsConfigAccessKeys = [
    { label: 'storageEditDialog.labels.accessKeyId', placeholder: '', description: 'storageEditDialog.descriptions.accessKeyId', controller: StorageDialogControllers.AccessKeyId, isRequired: true, type: InputTypeEnum.Password },
    { label: 'storageEditDialog.labels.secretAccessKey', placeholder: '', description: 'storageEditDialog.descriptions.secretAccessKey', controller: StorageDialogControllers.SecretAccessKey, isRequired: true, type: InputTypeEnum.Password },
  ];
  protected inputsConfig = [];
  protected providerOptions = Object.values(StorageConfigType);
  protected currentUidValue = '';
  protected storageConfigType = StorageConfigType;
  protected permissionLevelEnum = PermissionLevel;
  protected storageDialogControllers = StorageDialogControllers;
  protected storageCheckStatus = StorageCheckStatus;
  protected groupOptions = Object.values(this.data.groups).map(group => ({ value: group._id, label: group.label || group._id }))
  protected projectOptions = sortGroupedOptions(groupProjects(Object.values(this.data.projects)));
  protected isMalwareScanningEnabled = false;
  protected messages: LocaleCopy = {};

  constructor(
    @Inject(DIALOG_DATA) private data: DialogData,
    private coreApi: CoreApiService,
    private logger: LoggingService,
    private notifications: NotificationService,
    private dialogRef: DialogRef<XferStorage, DialogData>,
    private translate: TranslationService,
  ) {
    this.translate.getBundle('storageEditDialog').subscribe(messages => {
      this.messages = messages.copy;
    });
  }

  ngOnInit(): void {
    this.form = new FormGroup({
      [StorageDialogControllers.Label]: new FormControl('', [Validators.required, Validators.minLength(labelMinLength), Validators.maxLength(labelMaxLength)]),
      [StorageDialogControllers.ProviderType]: new FormControl(StorageConfigType.S3),
      [StorageDialogControllers.ProviderConfig]: this.getProviderFormGroup(StorageConfigType.S3),
      [StorageDialogControllers.PermissionLevel]: new FormControl(PermissionLevel.Site),
      [StorageDialogControllers.ContainerRef]: new FormControl({ value: '', disabled: true }),
      [StorageDialogControllers.ImportsEnabled]: new FormControl(true),
      [StorageDialogControllers.ExportsEnabled]: new FormControl(true),
      [StorageDialogControllers.MalwareScanning]: new FormControl(true),
    });
    this.form.get(StorageDialogControllers.MalwareScanning).disable();
    this.subscriptions.form = this.form.valueChanges.subscribe(() => {

      if (!this.storageId) {
        this.isValueChanged = true;
      } else {
        const currentValue = this.form.getRawValue();
        this.isValueChanged = !this.isEqualDeep(currentValue, this.initialFormValue);
      }

      if (this.form.dirty) {
        // stop checking status since it may not apply to current state of the form
        this.subscriptions.statusCheck.unsubscribe();
        this.statusCheck = null;
      }
    });

    if (this.storageId) {
      this.form.disable();
      this.coreApi.xfer.storages.get(this.storageId).pipe(map(storage => {
        this.storage = storage;
        this.form.enable();
        this.form.get(StorageDialogControllers.MalwareScanning).disable();
        this.form.reset({
          [StorageDialogControllers.Label]: storage.label,
          [StorageDialogControllers.ProviderType]: storage.config.type,
          [StorageDialogControllers.PermissionLevel]: storage.refs.project ? PermissionLevel.Project
            : storage.refs.group ? PermissionLevel.Group : PermissionLevel.Site,
          [StorageDialogControllers.ContainerRef]: storage.refs.project ?? storage.refs.group,
          [StorageDialogControllers.ImportsEnabled]: storage.imports_enabled,
          [StorageDialogControllers.ExportsEnabled]: storage.exports_enabled,
        });
        this.form.setControl(StorageDialogControllers.ProviderConfig, this.getProviderFormGroup(storage.config.type, storage.config));
        this.updateInputsConfig();
        this.disableEditFields();
      }), switchMap(() => {
        return this.getMalwareScanning();
      })).subscribe({
        next: () => {
          if (this.isMalwareScanningEnabled) {
            this.form.get(StorageDialogControllers.MalwareScanning).enable();
          }

          this.initialFormValue = this.form.getRawValue();
        },
        error: error => {
          this.logger.error(error);
        },
      });
    } else {
      this.getMalwareScanning().subscribe({
        next: () => {
          if (this.isMalwareScanningEnabled) {
            this.form.get(StorageDialogControllers.MalwareScanning).enable();
          }
        },
      });
      this.subscriptions.providerType = this.form.controls.providerType.valueChanges.subscribe((providerType: StorageConfigType) => {
        const previousConfig = this.form.controls.providerConfig.value as StorageConfig;
        this.form.setControl(StorageDialogControllers.ProviderConfig, this.getProviderFormGroup(providerType, previousConfig));
        this.updateInputsConfig();
      });
      this.subscriptions.permissionLevel = this.form.controls.permissionLevel.valueChanges.subscribe(permissionLevel => {
        const containerRef = this.form.controls.containerRef;
        if (permissionLevel === PermissionLevel.Site) {
          containerRef.disable();
        } else {
          containerRef.enable();
        }
      });
    }
    this.updateInputsConfig();
  }

  ngOnDestroy(): void {
    for (const key in this.subscriptions) {
      this.subscriptions[key].unsubscribe();
    }
  }

  get storageId(): string {
    return this.storage?._id ?? this.data.storageId;
  }

  get permissionLevel(): PermissionLevel {
    return this.form.controls.permissionLevel.value;
  }

  get projects(): Record<string, Project> {
    return this.data.projects;
  }

  get isSubmitButtonDisabled(): boolean {
    return !this.isValueChanged || this.form.invalid || this.form.pristine || this.isSaving || (!this.form.controls.importsEnabled.value) && !this.form.controls.exportsEnabled.value;
  }

  testConnection(): void {
    // kick off a new status check operation
    this.coreApi.xfer.storages.check(this.storageId).subscribe(response => {
      this.listenToStatusCheck({
        status: response.status,
        reason: response.status_reason,
      });
    });
  }

  saveChanges(): void {
    if (this.form.invalid || this.form.pristine || this.isSaving) {
      return;
    }

    this.isSaving = true;
    const configForm = this.form.controls.providerConfig.getRawValue();
    const initialConfig = this.initialFormValue?.providerConfig;
    const { uid, gid, ...config } = Object.keys(configForm)
      .filter((key: StorageDialogControllers) => configForm[key] !== null && !this.permissionsFields.includes(key))
      .reduce((obj, key: StorageDialogControllers) => {
        let value = configForm[key];
        value = typeof value === 'string' ? value.trim() : value;
        const isPasswordField = this.passwordFields.find(item => item === key);
        let initialValue = initialConfig?.[key];

        if (isPasswordField && initialValue === this.passwordInitialValue && value !== this.passwordInitialValue) {
          initialValue = '';
        }

        if (!this.storageId || initialValue !== value || this.chownFields.includes(key)) {
          return Object.assign(obj, { [snakeCase(key)]: value });
        }

        return obj;
      }, Object());

    const chown = this.formatChown(uid, gid);
    const initialChown = this.formatChown(initialConfig?.[StorageDialogControllers.Uid], initialConfig?.[StorageDialogControllers.Gid]);

    if (((!this.storageId && chown) || (this.storageId && chown !== initialChown)) && this.form.controls.providerType.value === StorageConfigType.FS) {
      config.chown = chown;
    }

    const payload: Partial<XferStorage> = {};

    if (!this.storageId || this.form.controls.label.value !== this.initialFormValue.label) {
      payload.label = this.form.controls.label.value;
    }

    if (!this.storageId || this.form.controls.importsEnabled.value !== this.initialFormValue.importsEnabled) {
      payload.imports_enabled = this.form.controls.importsEnabled.value;
    }

    if (!this.storageId || this.form.controls.exportsEnabled.value !== this.initialFormValue.exportsEnabled) {
      payload.exports_enabled = this.form.controls.exportsEnabled.value;
    }

    if (!this.storageId || this.form.get(StorageDialogControllers.MalwareScanning).value !== this.initialFormValue.malwareScanning) {
      payload.malware_scan = this.form.controls.malwareScanning.value;
    }

    if (this.form.get(StorageDialogControllers.ProviderType).value === StorageConfigType.FS) {
      const fsConfig = this.form.get(StorageDialogControllers.ProviderConfig);
      const chmod = fsConfig.get(StorageDialogControllers.UserPermission).value + fsConfig.get(StorageDialogControllers.GroupPermission).value + fsConfig.get(StorageDialogControllers.OtherPermission).value;
      const initialChmod = initialConfig?.[StorageDialogControllers.UserPermission] + initialConfig?.[StorageDialogControllers.GroupPermission] + initialConfig?.[StorageDialogControllers.OtherPermission];

      if ((!this.storageId && chmod !== this.chmodDefaultValue) || (this.storageId && chmod !== initialChmod)) {
        config.chmod = chmod;
      }
    }

    const configKeys = Object.keys(config);

    if (configKeys.length) {
      payload.config = config;
      payload.config.type = this.storageId ? this.storage.config.type : this.form.controls.providerType.value;
    }

    if (!this.storageId) {
      if (this.permissionLevel === PermissionLevel.Group) {
        payload.refs = {
          group: this.form.controls.containerRef.value,
        };
      }
      if (this.permissionLevel === PermissionLevel.Project) {
        const projectId = this.form.controls.containerRef.value;
        payload.refs = {
          group: this.projects[projectId].group,
          project: projectId,
        };
      }
    }

    const request$ = this.storageId
      ? this.coreApi.xfer.storages.patch(this.storageId, payload)
      : this.coreApi.xfer.storages.post(payload);
    request$.subscribe({
      next: storage => {
        this.notifications.success(`xfer.storages.${this.storageId ? 'modify' : 'create'}.success`);
        this.form.markAsPristine();
        this.disableEditFields();
        this.isSaving = false;
        this.hasSaved = true;
        this.storage = storage;
        this.initialFormValue = this.form.getRawValue();
        this.listenToStatusCheck({
          status: storage.status_check,
          reason: storage.status_check_reason,
        });
      },
      error: error => {
        this.logger.error(error);
        this.notifications.error(`xfer.storages.${this.storageId ? 'modify' : 'create'}.error`);

        const serverMessage = error?.error?.message;

        if (serverMessage) {
          const errorMessage = this.parseErrorMessage(error?.error?.message);

          if (errorMessage.msg) {
            this.notifications.error(errorMessage.msg, true);
          }
        }

        this.isSaving = false;
      },
    });
  }

  listenToStatusCheck(initialState?: StatusCheck): void {
    const loop$ = timer(STATUS_CHECK_INTERVAL * 1000).pipe(
      switchMap(() => this.coreApi.xfer.storages.get(this.storageId)),
      map(storage => ({
        status: storage.status_check,
        reason: storage.status_check_reason,
      })),
    );
    const init$ = initialState ? of(initialState) : loop$;
    this.subscriptions.statusCheck.unsubscribe();
    this.subscriptions.statusCheck = init$.pipe(
      expand(check => {
        // complete when status is success or failed
        if ([StorageCheckStatus.Success, StorageCheckStatus.Failed].includes(check.status)) {
          return EMPTY;
        }
        // otherwise wait 1s, request an update, and loop back through `expand`
        return loop$;
      }),
    ).subscribe(check => {
      this.statusCheck = check;
    });
  }

  protected close(): void {
    this.dialogRef.close(this.hasSaved ? this.storage : null);
  }

  protected hideToast(): void {
    this.statusCheck = null;
  }

  protected getProviderConfigControllerError(controller: StorageDialogControllers): string {
    const providerConfig = this.form.get(StorageDialogControllers.ProviderConfig);
    const formControl = providerConfig.get(controller);

    return this.getErrorText(formControl, controller)
  }

  protected onTextInputFocusOut(controller: StorageDialogControllers): void {
    const providerConfig = this.form.get(StorageDialogControllers.ProviderConfig);
    const formControl = providerConfig.get(controller);

    if (controller === StorageDialogControllers.Account) {
      const value = formControl.value?.trim();
      const domainPart = this.getAzureStorageDomainPart(value);

      if (value && !domainPart) {
        formControl.setValue(formControl.value + this.azureBlobSuffix)
      }

      return;
    }
  }

  protected removePasswordMask(name: StorageDialogControllers): void {
    if (!this.storageId || !this.passwordFields.includes(name)) {
      return;
    }

    const controller = this.form.get(StorageDialogControllers.ProviderConfig).get(name);

    if (controller.pristine && controller.value === this.passwordInitialValue) {
      controller.setValue(null);
    }
  }

  protected onUploadClick(): void {
    this.fileInput.nativeElement.click();
  }

  protected onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.readFileAsText(input.files[0], (value) => {
        this.form.get(StorageDialogControllers.ProviderConfig).get(StorageDialogControllers.ApplicationCredentials).setValue(value);
      })
    }
  }

  protected getLabelError(): string {
    return this.getErrorText(this.form.get(StorageDialogControllers.Label), StorageDialogControllers.Label);
  }

  private readFileAsText(file: File, callback: (value: string) => void): void {
    const reader = new FileReader();

    reader.onload = () => {
      const fileContent = reader.result as string;
      callback(fileContent);
    };

    reader.readAsText(file);
  }

  private updateInputsConfig(): void {
    switch (this.form.controls.providerType.value) {
      case StorageConfigType.S3:
        if (this.form.controls.providerConfig.get(StorageDialogControllers.ConnectorCreds).value) {
          this.inputsConfig = [...this.s3InputsConfigBase];
          break;
        }

        this.inputsConfig = [...this.s3InputsConfigBase, ...this.s3InputsConfigAccessKeys];
        break;
      case StorageConfigType.GS:
        this.inputsConfig = this.gsInputsConfig;
        break;
      case StorageConfigType.AZ:
        if (this.form.controls.providerConfig.get(StorageDialogControllers.ConnectorCreds).value) {
          this.inputsConfig = [...this.azInputsConfigBase];
          break;
        }
        this.inputsConfig = [...this.azInputsConfigBase, ...this.azInputsConfigAccessKey];
        break;
      default:
        this.inputsConfig = this.fsInputsConfig;
        break;
    }
  }

  private getProviderFormGroup(type: StorageConfigType, previous?: StorageConfig): FormGroup {
    const typedPrevious = { ...previous, type } as StorageConfig;
    this.subscriptions.connectorCreds.unsubscribe();

    switch (typedPrevious.type) {
      case StorageConfigType.FS: {
        // TODO remove uid/gid - implement reusable (here and imports/exports) chown controller
        const [uid, gid] = (typedPrevious?.chown ?? '').split(':');
        const form = new FormGroup({
          [StorageDialogControllers.Path]: new FormControl(typedPrevious?.path ?? '', Validators.required),
          [StorageDialogControllers.Uid]: new FormControl(uid ?? '', [typeNumber, this.validateUser]),
          [StorageDialogControllers.Gid]: new FormControl(gid ?? '', [typeNumber, this.validateGroup]),
          [StorageDialogControllers.Groups]: new FormControl(typedPrevious?.groups ?? null),
          [StorageDialogControllers.UserPermission]: new FormControl({ value: ChmodEnum.ReadWrite, disabled: true }),
          [StorageDialogControllers.GroupPermission]: new FormControl(typedPrevious?.chmod?.[1] ?? this.basePermissionDefaultOption.value),
          [StorageDialogControllers.OtherPermission]: new FormControl(typedPrevious?.chmod?.[2] ?? this.basePermissionDefaultOption.value),
        });

        if (this.storageId) {
          form.controls.path.disable();
        }
        return form;
      }
      case StorageConfigType.AZ: {
        const form = new FormGroup({
          [StorageDialogControllers.Account]: new FormControl(typedPrevious?.account ?? '', [Validators.required, this.azureBlobValidator()]),
          [StorageDialogControllers.Container]: new FormControl(typedPrevious?.container ?? '', Validators.required),
          [StorageDialogControllers.Prefix]: new FormControl(typedPrevious?.prefix ?? ''),
          [StorageDialogControllers.AccessKey]: new FormControl(this.storageId ? this.passwordInitialValue : null, this.storageId ? null : this.validateConnectorCreds),
          [StorageDialogControllers.ConnectorCreds]: new FormControl(typedPrevious?.connector_creds ?? false),
        });
        this.subscriptions.connectorCreds = form.controls.connectorCreds.valueChanges.subscribe(() => {
          form.controls.accessKey.reset(this.storageId ? this.passwordInitialValue : null);
          this.updateInputsConfig();
        });
        return form;
      }
      case StorageConfigType.GS: {
        const form = new FormGroup({
          [StorageDialogControllers.Bucket]: new FormControl(typedPrevious?.bucket ?? '', [Validators.required, Validators.maxLength(this.maxBucketNameLength), this.noForwardSlashValidator]),
          [StorageDialogControllers.Prefix]: new FormControl(typedPrevious?.prefix ?? ''),
          [StorageDialogControllers.ApplicationCredentials]: new FormControl(this.storageId ? this.passwordInitialValue : null, this.storageId ? null : Validators.required),
        });

        if (this.storageId) {
          form.controls.bucket.disable();
        }

        return form;
      }
      case StorageConfigType.S3: {
        const form = new FormGroup({
          [StorageDialogControllers.Bucket]: new FormControl(typedPrevious?.bucket ?? '', [Validators.required, Validators.maxLength(this.maxBucketNameLength), this.noForwardSlashValidator]),
          [StorageDialogControllers.Prefix]: new FormControl(typedPrevious?.prefix ?? ''),
          [StorageDialogControllers.AccessKeyId]: new FormControl(this.storageId ? this.passwordInitialValue : null, this.storageId ? null : this.validateConnectorCreds),
          [StorageDialogControllers.SecretAccessKey]: new FormControl(this.storageId ? this.passwordInitialValue : null, this.storageId ? null : this.validateConnectorCreds),
          [StorageDialogControllers.ConnectorCreds]: new FormControl(typedPrevious?.connector_creds ?? false),
        });

        this.subscriptions.connectorCreds = form.controls.connectorCreds.valueChanges.subscribe(() => {
          this.form.controls.providerConfig.get(StorageDialogControllers.AccessKeyId).reset();
          this.form.controls.providerConfig.get(StorageDialogControllers.SecretAccessKey).reset();
          this.updateInputsConfig();
        });

        if (this.storageId) {
          form.controls.bucket.disable();
        }

        return form;
      }
    }
  }

  private disableEditFields(): void {
    this.form.controls.permissionLevel.disable();
    this.form.controls.containerRef.disable();
  }

  private validateConnectorCreds(control: AbstractControl): ValidationErrors {
    // explicit creds only required if connectorCreds is false
    if (!control.parent) {
      return null;
    }
    return !control.value && !control.parent.get(StorageDialogControllers.ConnectorCreds).value ? { required: true } : null;
  }

  private validateUser(control: AbstractControl): ValidationErrors {
    if (!control.parent) {
      return null;
    }
    return !control.value && control.parent.get(StorageDialogControllers.Gid).value ? { required: true } : null;
  }

  private validateGroup(control: AbstractControl): ValidationErrors {
    if (control.parent) {
      control.parent.get(StorageDialogControllers.Uid).markAsTouched();
      control.parent.get(StorageDialogControllers.Uid).updateValueAndValidity();
    }
    return null;
  }

  private getMalwareScanning(): Observable<void> {
    return this.coreApi.xfer.connectors.query().pipe(map(result => {
      this.isMalwareScanningEnabled = result.results[0]?.info?.clamav;
      const malwareScanValue = this.storageId ? this.storage?.malware_scan !== false : true;
      this.form.get(StorageDialogControllers.MalwareScanning).setValue(this.isMalwareScanningEnabled ? malwareScanValue : false);
    }));
  }

  private isEqualDeep(val1: FormValue, val2: FormValue): boolean {
    if (typeof val1 !== 'object' || typeof val2 !== 'object' || val1 === null || val2 === null) {
      return val1 === val2;
    }

    const keys1 = Object.keys(val1);
    const keys2 = Object.keys(val2);

    if (keys1.length !== keys2.length) {
      return false;
    }

    for (const key of keys1) {
      let currentVal1 = val1[key];
      let currentVal2 = val2[key];

      if (this.storageId && this.passwordFields.find(item => item === key)) {
        currentVal1 = currentVal1 === this.passwordInitialValue ? '' : currentVal1;
        currentVal2 = currentVal2 === this.passwordInitialValue ? '' : currentVal2;
      }

      if (!keys2.includes(key) || !this.isEqualDeep(currentVal1, currentVal2)) {
        return false;
      }
    }

    return true;
  }

  private formatChown(uid: string, gid: string): string {
    return `${uid ?? ''}:${gid ?? ''}`.replace(/:$/, '').trim() || null;
  }

  private azureBlobValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const input = control.value.trim();

      const domainPart = this.getAzureStorageDomainPart(input);

      if (!domainPart) {
        return null;
      }

      if (domainPart === this.azureBlobSuffix && input.length > this.azureBlobSuffix.length) {
        return null;
      } else {
        return { errorWithInfo: true };
      }
    };
  }

  private getAzureStorageDomainPart(input: string): string {
    const dotIndex = input.indexOf('.');

    if (dotIndex === -1) {
      return null;
    }

    return input.substring(dotIndex);
  }

  private noForwardSlashValidator(control: AbstractControl): ValidationErrors | null {
    const value = control.value;

    if (typeof value === 'string' && value.includes('/')) {
      return { noForwardSlash: true };
    }

    return null;
  }

  private getErrorText(formControl: AbstractControl, controller: StorageDialogControllers): string {
    if (controller === StorageDialogControllers.Groups && this.currentUidValue) {
      this.isGroupsError = this.supplementalGroupIDValidators.some(validator => !validator(this.currentUidValue));

      return this.isGroupsError ? this.groupsValidatorsErrorMessage : '';
    }

    if (!formControl?.touched) {
      return '';
    }

    const errors = formControl.errors;
    if (!errors) {
      return '';
    }

    const messages = this.messages['errors'];

    if (this.numberInputs.includes(controller)) {
      return messages['isNotANumber'];
    }

    if (errors.required) {
      return messages['required'];
    }

    if (errors.noForwardSlash) {
      return messages['noForwardSlash'];
    }

    if (errors.maxlength) {
      return this.translate.interpolate(messages['maxLength'], errors.maxlength.requiredLength);
    }

    if (errors.minlength) {
      return this.translate.interpolate(messages['minLength'], errors.minlength.requiredLength);
    }

    if (errors.errorWithInfo) {
      return messages['account'];
    }

    return '';
  }

  private parseErrorMessage(message: string): Record<string, string> {
    const fixed = message
      .replace(/'/g, '"')
      .replace(/\(([^()]+)\)/g, (_, inside) => {
        const parts = inside
          .split(',')
          .map(p => p.trim())
          .filter(Boolean)
          .map(p => p.replace(/^"|"$/g, ''));
        return `[${parts.map(p => `"${p}"`).join(', ')}]`;
      });

    try {
      return JSON.parse(fixed)[0];
    }
    catch (error) {
      return {}
    }
  }

  protected readonly fields = ExportSettingsFields;
}
